import { gql } from 'apollo-boost';
import ExpandCardsWithFetch from 'core/components/common/ExpandCardsWithFetch';
import { SettingsContext } from 'core/components/settingsContext';
import { useFiltersFromUrl } from 'core/hooks/useFiltersFromUrl';
import { column, row, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import SearchEventManager from 'core/utils/EventManager';
import React, { useContext, useEffect, useMemo } from 'react';
import { ProductFormCard } from 'site/components/product-form';
import { ProductFormOrderEnum, ProductFormPaymentStatusEnum, useGetInCartFormsQuery } from 'site/graphql';

gql`
    query GetInCartForms($filter: ProductFormListRequest) {
        productForms(filter: $filter) {
            count
            result {
                id
                type
                price
                maxOrders
                ordersCount
                paymentStatus
                requiredFieldsNumber
                showNumberLessons
                order {
                    id
                }
                product {
                    id
                    name
                    shortDescription
                    offer
                    contentProvider {
                        id
                        offer
                    }
                    lecturers {
                        id
                        account {
                            id
                            firstName
                            lastName
                        }
                    }
                }
                withMeInSetForms {
                    id
                    type
                }
                webinars {
                    id
                    date
                }
                seminars {
                    id
                    date
                }
                records {
                    id
                    date
                }
            }
        }
    }
`;

type IInCartFormsProps = {
    searchEventManager: SearchEventManager;
} & WithStyles<typeof styles>;

const InCartForms: React.FC<IInCartFormsProps> = ({ searchEventManager, classes }) => {
    const settings = useContext(SettingsContext);
    const { search } = useFiltersFromUrl();
    const commonFilters = useMemo(
        () => ({
            contentProviderId: settings.accountPurchasedProducts.setContentProvider
                ? settings.contentProviderId
                : undefined,
            showPublic: settings.accountPurchasedProducts.showPublic,
            paymentStatus: ProductFormPaymentStatusEnum.Cart,
            order: ProductFormOrderEnum.OrderDate,
            excludeProductVisibilityLink: false,
            direction: 'desc',
            take: 3,
        }),
        [settings],
    );

    const { data, loading, refetch } = useGetInCartFormsQuery({
        fetchPolicy: 'network-only',
        variables: {
            filter: {
                ...commonFilters,
                search,
                take: 3,
            },
        },
    });
    const productForms = (data && data.productForms && data.productForms.result) || [];
    const count = (data && data.productForms && data.productForms.count) || 0;

    useEffect(() => {
        searchEventManager.subscribe((search: string) => {
            refetch({ filter: { ...commonFilters, search } });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        productForms.length > 0 && (
            <>
                <h2 className={classes.title}>Неоплаченные</h2>
                <ExpandCardsWithFetch
                    classes={{ root: classes.root, button: classes.button }}
                    items={productForms}
                    count={count}
                    collapseCount={3}
                    isFetching={loading}
                    fetchOnOpen={() => refetch({ filter: { ...commonFilters, full: true } })}
                    linkTitle={'Показать все неоплаченные курсы'}
                >
                    {productForm => (
                        <div className={classes.item} key={productForm.id}>
                            <ProductFormCard productForm={productForm} isDark mode={'IN_CART'} />
                        </div>
                    )}
                </ExpandCardsWithFetch>
            </>
        )
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        item: {
            ...column(),
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '1.5rem',
            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 6 }),
            },
            [theme.breakpoints.up('md')]: {
                ...column({ cols: 4 }),
            },
        },
        title: {
            ...title({ size: 'h2', mode: 'left' }),
        },
        root: {
            ...row(),
        },
        button: {
            marginLeft: 15,
            marginBottom: 1.5 + 'rem',

            [theme.breakpoints.down('xs')]: {
                marginLeft: 'auto',
            },
        },
    });

export default injectSheet(styles, 'AccountCourses')(InCartForms);
